/**
 * ============================================================================
 * DASHBOARD GATE — resolves the URL's dashboard slug and guards access.
 * ============================================================================
 *
 * The dashboard rides the URL path ("/kfe-wbr/...", spec multi-dashboard R5),
 * making links shareable and the URL the single source of truth for which
 * dashboard is active. This layout route sits above every page:
 *
 *   1. Parse the slug → dashboardId. Unknown slug → redirect to the default.
 *   2. Mirror it into the store (`dashboardChanged`) so every downstream
 *      consumer (data calls, headers, callout anchors) reads it from Redux as
 *      before — the URL just feeds the store now. Render nothing until the
 *      mirror catches up, so pages never fetch the previous dashboard's data
 *      for a tick.
 *   3. If GetPermissions says the dashboard is not accessible, render the
 *      access-denied state (with a Bindle request-access action) INSTEAD of
 *      the page — the header switcher stays mounted so the user can leave.
 *      Fails open while permissions load or when the API isn't deployed.
 *
 * The title (browser tab) is set here too: "<label> · <page>", matched off the
 * active structure's pages using the sub-path after the slug.
 */

import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import Box from '@amzn/awsui-components-core/box';
import Spinner from '@amzn/awsui-components-core/spinner';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import { dashboardChanged } from 'src/features/dashboard/dashboardSlice';
import {
  selectServedDashboards,
  useGetPermissionsQuery,
} from 'src/features/api/permissionsApi';
import { useActiveDashboard } from 'src/features/api/useActiveDashboard';
import {
  DEFAULT_DASHBOARD_SLUG,
  dashboardStructure,
  parseDashboardSlug,
  toDashboardSlug,
} from 'src/config/dashboards';
import { DashboardAccessDenied } from 'src/components/common/DashboardAccessDenied';

export const DashboardGate: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { dashboardSlug } = useParams<{ dashboardSlug: string }>();
  const dashboardId = parseDashboardSlug(dashboardSlug);

  const activeDashboardId = useAppSelector(
    (s) => s.dashboard.activeDashboardId,
  );
  const { data: permissions } = useGetPermissionsQuery();
  const { label } = useActiveDashboard();

  // Mirror the URL's dashboard into the store (only on change; the reducer
  // also clears the callout selection, which belongs to the other dashboard).
  useEffect(() => {
    if (dashboardId && dashboardId !== activeDashboardId) {
      dispatch(dashboardChanged(dashboardId));
    }
  }, [dashboardId, activeDashboardId, dispatch]);

  // Browser tab title: "<label> · <page>", matched off the sub-path.
  const structure = dashboardStructure(dashboardId ?? '');
  useEffect(() => {
    if (!dashboardId) return;
    const sub = location.pathname.replace(`/${dashboardSlug}`, '') || '/';
    const page =
      structure.pages.find(
        (p) => sub === p.route || sub.startsWith(`${p.route}/`),
      ) ?? structure.pages[0];
    document.title = page ? `${label} · ${page.title}` : label;
  }, [dashboardId, dashboardSlug, location.pathname, structure, label]);

  // Unknown slug → the default dashboard (nothing to request; it's a bad URL).
  if (!dashboardId) {
    return <Navigate to={`/${DEFAULT_DASHBOARD_SLUG}`} replace />;
  }

  // Legacy/non-canonical slug → the canonical URL, preserving the sub-path,
  // query, and hash so old bookmarks land on the same page (spec
  // dashboard-rename-kfe-quality R1.3): "/fai-wbr/detailed-view?tab=otd"
  // redirects to "/kfe-wbr/detailed-view?tab=otd". The slug's review-type
  // segment is preserved too (only the BASE canonicalizes): a future
  // "/fai-mbr" redirects to "/kfe-mbr", not to the WBR default — the slug
  // carries the review so multi-review needs no URL redesign (registry doc).
  const rawSlug = dashboardSlug!; // non-null: dashboardId parsed from it above
  const slugReviewType = rawSlug.includes('-')
    ? rawSlug.slice(rawSlug.lastIndexOf('-') + 1)
    : undefined;
  const canonicalSlug = toDashboardSlug(dashboardId, slugReviewType);
  if (rawSlug !== canonicalSlug) {
    const canonicalPath = location.pathname.replace(
      `/${rawSlug}`,
      `/${canonicalSlug}`,
    );
    return (
      <Navigate
        to={`${canonicalPath}${location.search}${location.hash}`}
        replace
      />
    );
  }

  // Wait for the store to mirror the URL so pages never fetch the prior
  // dashboard's data for a render.
  if (activeDashboardId !== dashboardId) {
    return (
      <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  // Accessibility from GetPermissions. Fail OPEN: an entry we can't see yet
  // (profile still loading, or API not deployed) renders the page; only a
  // definitive accessible:false shows the access-denied state.
  const entry = selectServedDashboards(permissions).find(
    (d) => d.dashboardId === dashboardId,
  );
  if (entry && !entry.accessible) {
    return (
      <DashboardAccessDenied
        label={label}
        requiredBindleLockId={entry.requiredBindleLockId}
      />
    );
  }

  return <Outlet />;
};
